import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import SharedFooter from "@/components/SharedFooter";
import { toast } from "sonner";
import { API } from "@/App";
import {
  Mail,
  Send,
  MessageCircle,
  Globe,
  CheckCircle,
  ExternalLink,
  CreditCard,
  LogIn,
  School,
} from "lucide-react";

export default function Contact() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    inquiryType: "general"
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubmitted(true);
        toast.success("Message sent successfully! We'll get back to you soon.");
      } else {
        toast.error("Failed to send message. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    }
    
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header - Consistent with LandingPage */}
      <nav className="relative z-10 px-4 sm:px-6 lg:px-8 py-2 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-1">
            <img 
              src="/eduzy-logo-optimized.png" 
              alt="Eduzy" 
              className="h-24 w-auto cursor-pointer"
              style={{ height: '6rem' }}
              data-testid="header-logo"
              onClick={() => navigate("/")}
            />
            <a 
              href="https://contentiumtech.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:opacity-100 opacity-80 transition-opacity"
            >
              <img 
                src="/contentium-tech-logo.svg" 
                alt="Contentium Tech" 
                className="h-auto"
                style={{ height: '4.5rem' }}
                data-testid="header-contentium-logo"
              />
            </a>
          </div>
          {/* Desktop Navigation */}
          <div className="hidden sm:flex items-center gap-2">
            <Button
              onClick={() => navigate("/schools")}
              variant="outline"
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold"
            >
              <School className="w-4 h-4 mr-2" />
              For Schools
            </Button>
            <Button
              onClick={() => navigate("/eduwise")}
              variant="outline"
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Pricing
            </Button>
            <Button
              onClick={() => navigate("/login")}
              variant="outline"
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Login
            </Button>
          </div>
          {/* Mobile Navigation */}
          <div className="sm:hidden">
            <Button
              onClick={() => navigate("/login")}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Login
            </Button>
          </div>
        </div>
      </nav>

      <main className="flex-1">
        {/* Contact Section */}
        <section className="py-16 px-4">
          <div className="max-w-5xl mx-auto">
            {/* Page Title */}
            <div className="text-center mb-12">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-3">Contact Us</h1>
              <p className="text-gray-600 max-w-xl mx-auto">
                Have questions about Eduzy? We'd love to hear from you.
              </p>
            </div>

            {/* Two Column Layout */}
            <div className="grid lg:grid-cols-2 gap-8">
              
              {/* Get in Touch */}
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4">
                  <h2 className="text-xl font-semibold text-white">Get in Touch</h2>
                </div>
                <div className="p-6 space-y-5">
                  {/* Email */}
                  <a 
                    href="mailto:info@contentiumtech.com" 
                    className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-emerald-50 transition-colors group"
                  >
                    <div className="w-11 h-11 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                      <Mail className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Email</p>
                      <p className="text-emerald-600">info@contentiumtech.com</p>
                    </div>
                  </a>

                  {/* WhatsApp */}
                  <a 
                    href="https://wa.me/919999064740" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-emerald-50 transition-colors group"
                  >
                    <div className="w-11 h-11 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                      <MessageCircle className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">WhatsApp</p>
                      <p className="text-emerald-600">+91 99990 64740</p>
                    </div>
                  </a>

                  {/* Websites */}
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50">
                    <div className="w-11 h-11 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Globe className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 mb-2">Websites</p>
                      <div className="space-y-1">
                        <a 
                          href="https://eduzy.co" 
                          className="text-emerald-600 hover:underline flex items-center gap-1"
                        >
                          eduzy.co <ExternalLink className="w-3 h-3" />
                        </a>
                        <a 
                          href="https://contentiumtech.com" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-emerald-600 hover:underline flex items-center gap-1"
                        >
                          contentiumtech.com <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Send us a Message */}
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4">
                  <h2 className="text-xl font-semibold text-white">Send us a Message</h2>
                </div>
                <div className="p-6">
                  {submitted ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-emerald-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">Message Sent!</h3>
                      <p className="text-gray-600 mb-6">
                        We'll get back to you within 24 hours.
                      </p>
                      <Button 
                        onClick={() => {
                          setSubmitted(false);
                          setFormData({
                            name: "",
                            email: "",
                            phone: "",
                            subject: "",
                            message: "",
                            inquiryType: "general"
                          });
                        }}
                        variant="outline"
                        className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                      >
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="name" className="text-gray-700">Full Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Your name"
                            required
                            className="mt-1.5 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-gray-700">Email *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="your@email.com"
                            required
                            className="mt-1.5 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="phone" className="text-gray-700">Phone</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+91 99999 99999"
                            className="mt-1.5 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                          />
                        </div>
                        <div>
                          <Label htmlFor="inquiryType" className="text-gray-700">Inquiry Type</Label>
                          <select
                            id="inquiryType"
                            name="inquiryType"
                            value={formData.inquiryType}
                            onChange={handleChange}
                            className="mt-1.5 w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          >
                            <option value="general">General Inquiry</option>
                            <option value="support">Technical Support</option>
                            <option value="billing">Billing Question</option>
                            <option value="school">School Partnership</option>
                            <option value="feedback">Feedback</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="subject" className="text-gray-700">Subject</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="What is this about?"
                          className="mt-1.5 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                        />
                      </div>

                      <div>
                        <Label htmlFor="message" className="text-gray-700">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="How can we help you?"
                          required
                          rows={4}
                          className="mt-1.5 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                        />
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full bg-emerald-600 hover:bg-emerald-700 h-11"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          "Sending..."
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer Note */}
        <section className="py-8 px-4 bg-white border-t border-gray-100">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-gray-600">
              <strong>Eduzy</strong> is a product of <strong>Contentium Tech</strong>.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              All communications and services related to Eduzy are managed by Contentium Tech.
            </p>
          </div>
        </section>
      </main>

      <SharedFooter />
    </div>
  );
}
